import React, { useMemo, useState } from 'react';
import BaseModal, {
	BaseModalProps,
} from '../../../../../Base/Components/BaseModal/BaseModal';
import {
	useCreateWorkplaceMedicalProceduresMutation,
	useLazyMedicalProceduresQuery,
	useWorkplaceMedicalProceduresQuery,
} from '../../../../../../graphql/graphql';
import useFlatData from '../../../../../Base/Hooks/useFlatData';
import useMedicalProcedures from '../../../../Hooks/useMedicalProcedures';
import { useAppContext } from '../../../../../../Services/AppContext';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import ManagerInsuranceCompaniesImportModalFooter from './ManagerInsuranceCompaniesImportModalFooter';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import ManagerInsuranceCompaniesImportModalFilter from './ManagerInsuranceCompaniesImportModalFilter';
import ManagerInsuranceCompaniesImportTable from './ManagerInsuranceCompaniesImportTable';
import { CustomSettingsContextProvider } from '../../../../../CustomSettings/Services/CustomSettingsContext';
import { CustomSettingKey } from '../../../../../Base/Enums/CustomSettingKey.enum';

const ManagerInsuranceCompaniesImportModal = (modalProps: BaseModalProps) => {
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [getMedicalProceduresQuery] = useLazyMedicalProceduresQuery();
	const [createWorkplaceMedicalProceduresMutation] =
		useCreateWorkplaceMedicalProceduresMutation();

	const { displayGQLError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const { workplaceId } = useAppContext();
	const {
		getMedicalProcedures,
		setPage,
		page,
		getNextPage,
		data: medicalProceduresData,
		totalPages,
	} = useMedicalProcedures(getMedicalProceduresQuery);

	const { data, isLoading: isLoadingWorkplaceMedicalProcedures } =
		useWorkplaceMedicalProceduresQuery({
			where: { workplaceId },
		});
	const workplaceMedicalProcedures =
		data?.workplaceMedicalProcedures?.data ?? [];

	const { flatData, loadData, hasMoreData, isLoading, setFilter } = useFlatData(
		getMedicalProcedures,
		{
			data: medicalProceduresData,
			totalPages,
			page,
		},
		page,
		setPage,
	);

	const medicalProceduresIds = useMemo(() => {
		return workplaceMedicalProcedures
			.map(
				workplaceMedicalProcedure =>
					workplaceMedicalProcedure?.medicalProcedure.id,
			)
			.filter((id): id is string => Boolean(id));
	}, [workplaceMedicalProcedures]);

	const toggleSelectedId = (id: string, value: boolean) => {
		setSelectedIds(prev => {
			const newSet = new Set(prev);
			if (value) {
				newSet.add(id);
			} else {
				newSet.delete(id);
			}
			return newSet;
		});
	};

	const handleSubmit = async () => {
		const nonDuplicateIds = Array.from(selectedIds).filter(
			id => !medicalProceduresIds.includes(id),
		);

		const input = nonDuplicateIds.map(medicalProcedureId => ({
			medicalProcedureId,
			workplaceId,
		}));

		setSelectedIds(prev => {
			const newSet = new Set(prev);
			nonDuplicateIds.forEach(id => {
				newSet.delete(id);
			});
			return newSet;
		});

		const result = await createWorkplaceMedicalProceduresMutation({ input });

		if ('error' in result) {
			displayGQLError(result);
		} else {
			showNotificationSuccess({ content: 'component.caseType.importSuccess' });
			modalProps.onClose();
		}
	};

	return (
		<BaseModal
			title={'component.caseType.manualImportTitle'}
			contentScroll={false}
			contentStyle={{ paddingBottom: 0 }}
			footer={
				<ManagerInsuranceCompaniesImportModalFooter onSubmit={handleSubmit} />
			}
			{...modalProps}>
			<CustomSettingsContextProvider
				customSettingsKey={CustomSettingKey.MEDICAL_PROCEDURES_IMPORT}>
				<ManagerInsuranceCompaniesImportModalFilter setFilter={setFilter} />
				<ManagerInsuranceCompaniesImportTable
					data={flatData}
					isLoading={isLoading || isLoadingWorkplaceMedicalProcedures}
					onRefresh={() => loadData(true)}
					onLoadMore={getNextPage}
					hasMoreData={hasMoreData}
					selectedIds={selectedIds}
					toggleSelectedId={toggleSelectedId}
				/>
			</CustomSettingsContextProvider>
		</BaseModal>
	);
};

export default ManagerInsuranceCompaniesImportModal;
